import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {fetchRecentDrivingEvents} from "./actions";
import {IDrivingEvent} from "../../../models/IDrivingEvent";

/**
 * Лимиты, чтобы стейт не разрастался.
 * Если нужны исторические данные — берём с бэка через HTTP, а не из слайса.
 */
const MAX_FEED_SIZE = 200;          // глобальная лента "что прямо сейчас"
const MAX_PER_MACHINERY = 100;      // лента по конкретной машине

interface IDrivingEventsState {
    /** Глобальная лента последних событий (отсортирована по времени, новые сверху) */
    feed: IDrivingEvent[];
    /** События, сгруппированные по machinery_id (для попапа/детального экрана) */
    byMachinery: Record<string, IDrivingEvent[]>;
    /** Подключение к комнате событий */
    isConnected: boolean;
    /** Идёт первичная загрузка истории */
    isLoading: boolean;
    /** Ошибка последней загрузки истории */
    error: string | null;
}

const initialState: IDrivingEventsState = {
    feed: [],
    byMachinery: {},
    isConnected: false,
    isLoading: false,
    error: null,
};

/** Вставка с поддержанием сортировки по recorded_at DESC + ограничение размера */
function insertSorted(list: IDrivingEvent[], event: IDrivingEvent, max: number): IDrivingEvent[] {
    // Дубль (например, реконнект и повторная доставка) — игнорируем
    if (list.some(e => e.id === event.id)) return list;

    const eventTime = new Date(event.recorded_at).getTime();
    const insertIdx = list.findIndex(e => new Date(e.recorded_at).getTime() < eventTime);

    const next = insertIdx === -1
        ? [...list, event]
        : [...list.slice(0, insertIdx), event, ...list.slice(insertIdx)];

    return next.length > max ? next.slice(0, max) : next;
}

export const DrivingEventsSlice = createSlice({
    name: "driving_events_slice",
    initialState,
    reducers: {
        setDrivingEventsConnected: (state, action: PayloadAction<boolean>) => {
            state.isConnected = action.payload;
        },

        setDrivingEventsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

        /**
         * Загрузить начальную историю при подключении (если бэк её отдаёт).
         * Полностью перезаписывает feed и byMachinery.
         */
        setDrivingEventsHistory: (state, action: PayloadAction<IDrivingEvent[]>) => {
            const sorted = [...action.payload].sort(
                (a, b) => new Date(b.recorded_at).getTime() - new Date(a.recorded_at).getTime()
            );

            state.feed = sorted.slice(0, MAX_FEED_SIZE);

            const grouped: Record<string, IDrivingEvent[]> = {};
            for (const ev of sorted) {
                if (!grouped[ev.machinery_id]) grouped[ev.machinery_id] = [];
                if (grouped[ev.machinery_id].length < MAX_PER_MACHINERY) {
                    grouped[ev.machinery_id].push(ev);
                }
            }
            state.byMachinery = grouped;
            state.isLoading = false;
        },

        /**
         * Добавить одно событие, пришедшее из WebSocket.
         * Поддерживает сортировку по времени и ограничивает размер.
         */
        addDrivingEvent: (state, action: PayloadAction<IDrivingEvent>) => {
            const event = action.payload;

            state.feed = insertSorted(state.feed, event, MAX_FEED_SIZE);

            const machineryList = state.byMachinery[event.machinery_id] ?? [];
            state.byMachinery[event.machinery_id] = insertSorted(
                machineryList,
                event,
                MAX_PER_MACHINERY,
            );
        },

        /** Очистить ленту конкретной машины (например, "отметить как просмотренные") */
        clearMachineryEvents: (state, action: PayloadAction<string>) => {
            delete state.byMachinery[action.payload];
        },

        /** Полный сброс — например, при логауте или переключении компании */
        resetDrivingEvents: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRecentDrivingEvents.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchRecentDrivingEvents.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;

                const sorted = [...action.payload].sort(
                    (a, b) => new Date(b.recorded_at).getTime() - new Date(a.recorded_at).getTime()
                );

                state.feed = sorted.slice(0, MAX_FEED_SIZE);

                const grouped: Record<string, IDrivingEvent[]> = {};
                for (const ev of sorted) {
                    if (!grouped[ev.machinery_id]) grouped[ev.machinery_id] = [];
                    if (grouped[ev.machinery_id].length < MAX_PER_MACHINERY) {
                        grouped[ev.machinery_id].push(ev);
                    }
                }
                state.byMachinery = grouped;
            })
            .addCase(fetchRecentDrivingEvents.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload ?? 'Failed to load events';
            });
    },
});

export const {
    setDrivingEventsConnected,
    setDrivingEventsLoading,
    setDrivingEventsHistory,
    addDrivingEvent,
    clearMachineryEvents,
    resetDrivingEvents,
} = DrivingEventsSlice.actions;

export default DrivingEventsSlice.reducer;